import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';

import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DateRange } from 'react-day-picker';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMetricsDataEnhanced } from './hooks/useMetricsDataEnhanced';
import { buildPresetRange } from '@/utils/period-presets';
import { EnhancedMetricsHeader } from './dashboard/EnhancedMetricsHeader';
import { TabContent } from './dashboard/TabContent';
import { MetricsStyles } from './dashboard/MetricsStyles';
import { MetricsFooter } from './dashboard/MetricsFooter';
import {
  BarChart3,
  LineChart,
  TrendingDown,
  Clock,
  Globe,
  MapPin,
  Smartphone,
  Chrome,
  Filter,
  ExternalLink,
} from 'lucide-react';
import './metrics.css';
import { FunnelTabContent } from './dashboard/tabs/FunnelTabContent';

// Tab configuration with icons
const TABS_CONFIG = [
  { value: 'views', get label() { return translateInterfaceText("Visualizações"); }, icon: LineChart },
  { value: 'retention', get label() { return translateInterfaceText("Retenção"); }, icon: TrendingDown },
  { value: 'prime-times', get label() { return translateInterfaceText("Horários"); }, icon: Clock },
  { value: 'funnel', get label() { return translateInterfaceText("Funil"); }, icon: Filter },
  { value: 'traffic', get label() { return translateInterfaceText("Origem"); }, icon: Globe },
  { value: 'reference', get label() { return translateInterfaceText("Referência"); }, icon: ExternalLink },
  { value: 'regional', get label() { return translateInterfaceText("Locais"); }, icon: MapPin },
  { value: 'devices', get label() { return translateInterfaceText("Dispositivos"); }, icon: Smartphone },
  { value: 'browsers', get label() { return translateInterfaceText("Navegadores"); }, icon: Chrome },
];

export function MetricsDashboard({ initialVideoId }: { initialVideoId?: string | null }) {
  useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('views');
  // Inicializar com o vídeo da URL (se houver)
  const [activeVideoId, setActiveVideoId] = useState<string | null>(initialVideoId || null);
  const [activeVideoMP4, setActiveVideoMP4] = useState<string | null>(null);
  const [date, setDate] = useState<DateRange | undefined>(() =>
    buildPresetRange('last30Days', new Date())
  );

  // Sincronizar activeVideoId com initialVideoId quando a URL muda
  React.useEffect(() => {
    if (initialVideoId && initialVideoId !== activeVideoId) {
      setActiveVideoId(initialVideoId);
    }
  }, [initialVideoId]);

  // Hook centralizado para evitar múltiplas chamadas à API
  const metricsEnhanced = useMetricsDataEnhanced(date, activeVideoId);

  // Atualizar activeVideoMP4 quando os dados de métricas mudarem
  React.useEffect(() => {
    if (metricsEnhanced?.videoInfo?.video_mp4) {
      setActiveVideoMP4(metricsEnhanced.videoInfo.video_mp4);
    }
  }, [metricsEnhanced?.videoInfo]);

  // Derivar dados do metricsEnhanced para compatibilidade
  const isLoading = metricsEnhanced.isLoading;

  const handleVideoSelect = (videoId: string, videoMP4?: string | null) => {
    setActiveVideoId(videoId);
    setActiveVideoMP4(videoMP4 || null);

    // Atualizar a URL para refletir o vídeo selecionado
    setSearchParams({ videoId });
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden w-full">
      <MetricsStyles />

      {/* Header - Mobile: 16px padding, Desktop: 32px */}
      <div className="flex-shrink-0 bg-transparent">
        {/* Título */}
        <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 pt-4 md:pt-4 pb-4 md:pb-5">
          <div className="flex items-center gap-2.5">
            <BarChart3 className="h-[18px] w-[18px] text-[#2D6CF6]" strokeWidth={2} />
            <h1 className="metrics-header-title text-[20px] md:text-[22px] font-semibold text-[#1E2330] tracking-tight leading-[1.2]">
              {translateInterfaceText("Métricas do Vídeo")}</h1>
          </div>
        </div>

        {/* Linha divisória que preenche toda a largura */}
        <div className="hidden lg:block h-px w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }} />

        {/* Filtros */}
        <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8">
          <div className="flex flex-row items-center justify-end gap-3 pt-4 md:pt-6 pb-4 md:pb-5">
            <EnhancedMetricsHeader
              selectedVideoId={activeVideoId}
              onSelectVideo={handleVideoSelect}
              date={date}
              onDateChange={setDate}
              videoData={null}
              isLoading={isLoading}
            />
          </div>
        </div>
      </div>

      <Tabs
        defaultValue="views"
        value={activeTab}
        onValueChange={setActiveTab}
        className="flex-1 flex flex-col overflow-hidden"
      >
        {/* Tabs Premium PRD - Mobile: chips compactos, Desktop: full tabs */}
        <div className="flex-shrink-0 bg-transparent">
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8">
            <TabsList className="metrics-tabs-premium flex w-full lg:grid lg:grid-cols-9 gap-1" role="tablist">
              {TABS_CONFIG.map((tab) => {
                const IconComponent = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    role="tab"
                    aria-controls={`${tab.value}-panel`}
                    className="metrics-tab-trigger"
                  >
                    <IconComponent className="metrics-tab-icon" />
                    <span>{tab.label}</span>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>
        </div>

        {/* Conteúdo das abas com scroll - Mobile: padding 16px, Desktop: 32px */}
        <div className="flex-1 overflow-y-auto metrics-scroll">
          <div className="max-w-[1600px] w-full mx-auto px-4 md:px-8 py-6 md:py-8 pt-5 md:pt-8">
            <TabsContent value="views" role="tabpanel" id="views-panel" tabIndex={0} className="mt-0 space-y-6 focus:outline-none">
              <TabContent
                activeTab="views"
                isLoading={isLoading}
                date={date}
                activeVideoId={activeVideoId}
                metricsEnhanced={metricsEnhanced}
              />
            </TabsContent>

            <TabsContent value="retention" role="tabpanel" id="retention-panel" tabIndex={0} className="mt-0 space-y-6 focus:outline-none">
              <TabContent
                activeTab="retention"
                isLoading={isLoading}
                date={date}
                activeVideoId={activeVideoId}
                videoMP4={activeVideoMP4}
                metricsEnhanced={metricsEnhanced}
              />
            </TabsContent>

            <TabsContent value="prime-times" role="tabpanel" id="prime-times-panel" tabIndex={0} className="mt-0 space-y-6 focus:outline-none">
              <TabContent
                activeTab="prime-times"
                isLoading={isLoading}
                date={date}
                activeVideoId={activeVideoId}
                metricsEnhanced={metricsEnhanced}
              />
            </TabsContent>

            <TabsContent value="funnel" role="tabpanel" id="funnel-panel" tabIndex={0} className="mt-0 space-y-6 focus:outline-none">
              <FunnelTabContent metricsEnhanced={metricsEnhanced} />
            </TabsContent>

            <TabsContent value="traffic" role="tabpanel" id="traffic-panel" tabIndex={0} className="mt-0 space-y-6 focus:outline-none">
              <TabContent
                activeTab="traffic"
                isLoading={isLoading}
                date={date}
                activeVideoId={activeVideoId}
                metricsEnhanced={metricsEnhanced}
              />
            </TabsContent>

            <TabsContent value="reference" role="tabpanel" id="reference-panel" tabIndex={0} className="mt-0 space-y-6 focus:outline-none">
              <TabContent
                activeTab="reference"
                isLoading={isLoading}
                date={date}
                activeVideoId={activeVideoId}
                metricsEnhanced={metricsEnhanced}
              />
            </TabsContent>

            <TabsContent value="regional" role="tabpanel" id="regional-panel" tabIndex={0} className="mt-0 space-y-6 focus:outline-none">
              <TabContent
                activeTab="regional"
                isLoading={isLoading}
                date={date}
                activeVideoId={activeVideoId}
                metricsEnhanced={metricsEnhanced}
              />
            </TabsContent>

            <TabsContent value="devices" role="tabpanel" id="devices-panel" tabIndex={0} className="mt-0 space-y-6 focus:outline-none">
              <TabContent
                activeTab="devices"
                isLoading={isLoading}
                date={date}
                activeVideoId={activeVideoId}
                metricsEnhanced={metricsEnhanced}
              />
            </TabsContent>

            <TabsContent value="browsers" role="tabpanel" id="browsers-panel" tabIndex={0} className="mt-0 space-y-6 focus:outline-none">
              <TabContent
                activeTab="browsers"
                isLoading={isLoading}
                date={date}
                activeVideoId={activeVideoId}
                metricsEnhanced={metricsEnhanced}
              />
          </TabsContent>
          </div>
        </div>
      </Tabs>
    </div>
  );
}
